import { CheckCircle } from "@mui/icons-material";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IRangeDate, useCompanyQuery } from "../../../../../api/services";
import { FormTextField } from "../../../../../components/fields";
import { IFormValues, InsertBookingStep } from "../../../types";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    businessAgreementCheckValidate,
    businessAgreementCig,
    mandatoryDossierIsRequired,
    rangeDateValidity,
    stepState
} from "../../../recoil";

const FormCustomerCode = () => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    const step = useRecoilValue(stepState);
    const setBusinessAgreementCheckValidate = useSetRecoilState<boolean | undefined>(businessAgreementCheckValidate);
    const setBusinessAgreementHasCig = useSetRecoilState<boolean | undefined>(businessAgreementCig);
    const seMandatoryDossierIsRequired = useSetRecoilState<boolean | undefined>(mandatoryDossierIsRequired);
    const setRangeDate = useSetRecoilState<IRangeDate | undefined>(rangeDateValidity);

    const [isValidated, setIsValidated] = useState(false);

    const customerCode = useWatch<IFormValues, 'basicDetails.customerCode'>({
        name: 'basicDetails.customerCode'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const pickupDate = useWatch<IFormValues, 'basicDetails.pickupDate'>({
        name: 'basicDetails.pickupDate'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const companyQuery = useCompanyQuery(
        {
            code: customerCode,
            segment: rentalType,
            pickupDate: pickupDate?.isValid() ? pickupDate?.toISOString() : '',
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            enabled: false,
            onSuccess: (companyFound) => {
                setBusinessAgreementCheckValidate(false);
                setBusinessAgreementHasCig(!!companyFound?.cig && companyFound?.cig?.length > 0);
                seMandatoryDossierIsRequired(companyFound?.mandatoryDossier);
                setRangeDate(companyFound?.rangeDate);

                if (!companyFound && formContext.control.getFieldState('basicDetails.customerCode').isDirty) {
                    formContext.setError(
                        'basicDetails.customerCode',
                        {
                            message: 'message.validation.invalid_code'
                        }
                    );
                    return;
                }

                setBusinessAgreementCheckValidate(true);
                formContext.clearErrors('basicDetails.customerCode');
                setIsValidated(!!companyFound);
            },
            onError: () => {
                console.log('companyQuery onError');
                setIsValidated(false);
                setBusinessAgreementCheckValidate(false);
                formContext.setError(
                    'basicDetails.customerCode',
                    {
                        message: 'message.validation.invalid_code'
                    }
                );
            },
        }
    );

    const handleCheck = useCallback(() => {
        try {
            setIsValidated(false);
            companyQuery.refetch();
        } catch (error) {
            console.error("Errore in handleKeyDown:", error);
        }
    }, []);

    const handleKeyDown = useCallback(async () => {
        try {
            setIsValidated(false);
            formContext.resetField('basicDetails.pickupLocation');
            formContext.resetField('basicDetails.dropoffLocation');
            formContext.resetField('basicDetails.carGroup');
            formContext.resetField('basicDetails.mileageIncluded');
            formContext.resetField('basicDetails.cig');
            // companyQuery.remove();

            //setBusinessAgreementHasCig(!!companyQuery.data?.cig && companyQuery.data?.cig?.length > 0);
            setBusinessAgreementHasCig(false);
            seMandatoryDossierIsRequired(false);
            setRangeDate({ startDate: null, endDate: null });
            setBusinessAgreementCheckValidate(false);
        } catch (error) {
            console.error("Errore in handleKeyDown:", error);
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (!customerCode || step === InsertBookingStep.CUSTOMER_DETAILS) {
            return undefined;
        }
        if (companyQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        return (
            <InputAdornment position="end">
                <IconButton onClick={handleCheck}>
                    <CheckCircle sx={{
                        fill: (isValidated && companyQuery.data && companyQuery.data.name) ? 'green' : undefined
                    }} />
                </IconButton>
            </InputAdornment>
        );
    }, [customerCode, step, companyQuery.isFetching, isValidated]);

    return (
        <FormTextField<IFormValues>
            name="basicDetails.customerCode"
            label={t('label.businessAgreement')}
            onKeyDown={handleKeyDown}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
            helperText={isValidated && companyQuery.data && companyQuery.data.name
                ? `${companyQuery.data.name} - ${t(`creditType.${companyQuery.data.creditType}`)}`
                : ''}
        />
    );
};

export default FormCustomerCode;
